<script setup lang="ts">
import type { BreadcrumbItem } from '~/components/ui/breadcrumb';

import { clearAgentProfileEntry } from '~/composables/useAgentProfileNavigation';
import { useLogger } from '~/composables/useLogger';
import { EventBusKeys } from '~/core/helpers/symbols';
import { useAgentProfileStore } from '~/store/agentProfileStore';
import { useNavigationStore } from '~/store/navigationStore';
import { nuxtAgentProfilesRepository } from '~~/adapters/repositories/nuxt/nuxtAgentProfilesRepository';
import { storeToRefs } from 'pinia';

import {
	AgentExclusivePropertiesSection,
	AgentExclusivePropertiesSectionSkeleton
} from '~/components/Sections/AgentExclusivePropertiesSection';
import { AgentHeroSection } from '~/components/Sections/AgentHeroSection';
import {
	AgentPortfolioSection,
	AgentPortfolioSectionSkeleton
} from '~/components/Sections/AgentPortfolioSection';
import {
	AgentShortsSection,
	AgentShortsSectionSkeleton
} from '~/components/Sections/AgentShortsSection';
import { BrokerAboutSection } from '~/components/Sections/BrokerAboutSection';
import { BrokerPerformanceSection } from '~/components/Sections/BrokerPerformanceSection';
import { BrokerProfileCardSection } from '~/components/Sections/BrokerProfileCardSection';

definePageMeta({
	layout: 'agent-profile',
	key: route => route.path
});

const { ORIGIN } = useRuntimeConfig().public;
const { slug } = useRoute('corretor-slug').params;
const logger = useLogger('AgentProfilePage');

const agentProfileStore = useAgentProfileStore();
const activePageId = useState('agent-profile-active-page-id', () => 0);
const pageId = ++activePageId.value;
let isDisposed = false;
onScopeDispose(() => {
	isDisposed = true;
});

// Each page owns its store updates and cleanup, even when navigation returns
// to the same slug before the previous instance finishes unmounting.
agentProfileStore.reset();
agentProfileStore.setSlug(slug);

const {
	data: agentProfileResult,
	status,
	error: requestError
} = await useAsyncData(
	`agent-profile-${slug}`,
	(_nuxtApp, { signal }) =>
		nuxtAgentProfilesRepository.getProfileBySlug(slug, { signal }),
	{
		server: true,
		lazy: true,
		default: () => null
	}
);

const isProfileLoading = computed(
	() => status.value === 'idle' || status.value === 'pending'
);

function applyProfileResult() {
	if (
		isDisposed ||
		activePageId.value !== pageId ||
		agentProfileStore.slug !== slug ||
		isProfileLoading.value
	)
		return;
	const [agentProfile, agentProfileError] = agentProfileResult.value ?? [
		null,
		null
	];
	if (requestError.value || agentProfileError || !agentProfile) {
		const statusCode =
			requestError.value?.statusCode ||
			(agentProfileError?.originalErrorObject as { statusCode?: number } | null)
				?.statusCode ||
			500;

		logger.error('Failed to load agent profile', {
			agent_slug: slug,
			error_status: statusCode,
			error:
				requestError.value ??
				agentProfileError ??
				new Error('Agent profile response was empty')
		});

		return createError({
			statusCode,
			statusMessage:
				statusCode === 404
					? `Corretor com slug '${slug}' não foi encontrado`
					: agentProfileError?.message ||
						'Erro ao carregar o perfil do corretor',
			fatal: true
		});
	}

	agentProfileStore.setProfile(agentProfile);
}

// Preserve server-rendered metadata and HTTP errors on direct visits, while
// client navigation can render the skeleton before the request settles.
const initialError = applyProfileResult();
if (initialError) throw initialError;
watch([status, agentProfileResult, requestError], () => {
	const error = applyProfileResult();
	if (error) showError(error);
});

const { profile, company } = storeToRefs(agentProfileStore);

const navigationStore = useNavigationStore();

useAgentContextualMenu();

const isContactModalOpen = ref(false);

function handleCardSubmit() {
	useEventBus<void>(EventBusKeys.openContactModalAtOtp).emit();
}

const modalBroker = computed(() =>
	profile.value
		? {
				id: profile.value.id,
				name: profile.value.name,
				profilePicture: profile.value.profilePicture
			}
		: undefined
);

const breadCrumbItems = computed<BreadcrumbItem[]>(() => {
	if (!profile.value) return [];

	return [
		{
			label: 'Início',
			route: '/',
			type: 'home',
			separator: true
		},
		{
			label: profile.value.name,
			route: `/corretor/${profile.value.slug}`,
			separator: false
		}
	];
});

const seoDescription = computed<string>(() => {
	if (!profile.value) return '';

	return (
		profile.value.bio ||
		`Conheça o perfil de ${profile.value.name} e os imóveis anunciados por ${company.value?.name || 'este corretor'} na PilarHomes.`
	);
});

useHead({
	meta: [
		{
			property: 'og:url',
			content: `${ORIGIN}/corretor/${slug}`
		}
	]
});

useSeoMeta({
	title: () => `${profile.value?.name || 'Perfil do corretor'} | PilarHomes`,
	description: () => seoDescription.value,
	ogTitle: () => `${profile.value?.name || 'Perfil do corretor'} | PilarHomes`,
	ogDescription: () => seoDescription.value,
	ogImage: () => profile.value?.profilePicture.url || undefined
});

onUnmounted(() => {
	if (activePageId.value !== pageId || agentProfileStore.slug !== slug) return;

	agentProfileStore.reset();
	clearAgentProfileEntry();
});
</script>

<template>
	<div
		class="flex min-h-dvh w-full flex-col bg-white"
		data-testid="agent-profile-page"
		:aria-busy="isProfileLoading"
	>
		<p v-if="isProfileLoading" role="status" class="sr-only">
			Carregando perfil do corretor
		</p>
		<AgentHeroSection ref="heroSectionRef" :is-loading="isProfileLoading" />
		<Container class="flex-1 bg-white pt-8 md:pt-8 lg:pt-11 2xl:px-32!">
			<div class="flex flex-col gap-8 lg:flex-row lg:justify-between">
				<div class="flex min-w-0 flex-1 flex-col [&>*:first-child]:mt-0">
					<BrokerAboutSection
						id="agent-about"
						ref="aboutRef"
						data-section-name="Sobre"
						data-section-priority="1"
						class="mt-2"
						:is-loading="isProfileLoading"
					/>

					<AgentExclusivePropertiesSectionSkeleton
						v-if="isProfileLoading"
						class="mt-8 md:mt-12"
					/>
					<AgentExclusivePropertiesSection
						v-else
						id="agent-exclusives"
						ref="exclusivesRef"
						data-section-name="Exclusivos"
						class="mt-8 md:mt-12"
						:agent-slug="slug"
					/>

					<AgentShortsSectionSkeleton v-if="isProfileLoading" />
					<AgentShortsSection
						v-else
						id="agent-shorts"
						ref="shortsRef"
						data-section-name="Shorts"
						:agent-slug="slug"
					/>

					<BrokerPerformanceSection
						id="agent-performance"
						ref="performanceRef"
						data-section-name="Atuação e resultados"
						:is-loading="isProfileLoading"
						class="mt-8 md:mt-12"
					/>

					<AgentPortfolioSectionSkeleton v-if="isProfileLoading" />
					<AgentPortfolioSection
						v-else
						id="agent-portfolio"
						ref="portfolioRef"
						data-section-name="Portfólio de Imóveis"
						:agent-slug="slug"
					/>

					<BrokerProfileCardSection
						id="agent-contact"
						ref="profileCardRef"
						data-section-name="Atendimento"
						class="mt-8 md:mt-12"
						:is-loading="isProfileLoading"
						@contact-click="isContactModalOpen = true"
					/>
				</div>

				<aside
					class="hidden w-full lg:block lg:min-w-[330px] lg:max-w-[330px] lg:shrink-0 xl:max-w-[400px]"
				>
					<div
						class="transition-all duration-300 ease-in-out lg:sticky"
						:class="{
							'lg:top-[181px]': navigationStore.isHeaderVisible,
							'lg:top-[103px]': !navigationStore.isHeaderVisible
						}"
					>
						<SectionsAgentContactSection
							:is-loading="isProfileLoading"
							@submit="handleCardSubmit"
						/>
					</div>
				</aside>
			</div>
		</Container>

		<SectionsAgentContactBarSection
			v-if="!isProfileLoading"
			@contact-click="isContactModalOpen = true"
		/>

		<LazyContactFormModal
			v-if="!isProfileLoading"
			v-model="isContactModalOpen"
			context="agent"
			:agent-slug="profile?.slug"
			:broker="modalBroker"
			:company-name="company?.name"
			:company-logo="company?.smallLogo"
		/>

		<div class="bg-white-dark-1">
			<Container class="py-6! md:py-8!">
				<LazyVisibleBreadcrumb hydrate-on-visible :items="breadCrumbItems" />
			</Container>
		</div>
	</div>
</template>
